import { Route } from '@angular/router';
import { AuthGuard, LoginGuard } from '@insite-store360/auth';
import { LoginComponent } from '@insite-store360/UI';
import { ImageDisplayComponent } from './module/image-display/image-display.component';

export const appRoutes: Route[] = [
  /**
   * PUBLIC dealer self-registration, reached by link.
   *
   * No `canActivate` — deliberately. The visitor is a dealer who does not have
   * an account yet, which is the entire premise; an AuthGuard here would send
   * every one of them to a login screen they cannot pass.
   *
   * ⚠️ DECLARED FIRST, above the two `path: ''` entries. Those match by prefix
   * and carry LoginGuard / AuthGuard; the router does backtrack out of a
   * non-matching lazy child, but relying on that to keep a public page public
   * is a subtlety nobody should have to rediscover. First is unambiguous.
   *
   * The `e-warranty/` prefix groups it with the module it belongs to. It is
   * NOT under `/admin`, which is where the guarded portal lives.
   */
  {
    path: 'e-warranty/dealer-registration',
    loadComponent: () =>
      import('./module/dealer-registration/dealer-registration.component').then(
        (m) => m.DealerRegistrationComponent
      ),
  },
  /**
   * The COMMON verification page: one URL for every applicant, where they type
   * the reference ID they were given at submit.
   *
   * Declared BEFORE the parameterised route below, or that one would match
   * this path with an empty segment. It is the same component; `data.mode`
   * is what tells it to ask for a reference rather than assume this browser
   * filed the application.
   *
   * The point of a page with no per-applicant part in its URL is that it can be
   * printed, bookmarked and handed out by support, and never expires — which is
   * exactly what the one-time link it replaced could not do.
   */
  {
    path: 'e-warranty/dealer-registration/status',
    data: { mode: 'lookup' },
    loadComponent: () =>
      import('./module/dealer-registration/dealer-registration.component').then(
        (m) => m.DealerRegistrationComponent
      ),
  },
  /**
   * The same view, deep-linked. The parameter carries EITHER a reference ID or
   * — for links already sent before the rework — a phase-19 tracking token; the
   * component tells them apart by shape and resolves each through its own
   * endpoint, so an old link still opens, and lands on a view that shows its
   * holder the reference ID to use from now on.
   *
   * There is still no "look up by phone number" route, here or on the server:
   * that would let anyone test any number to see who had applied and read the
   * tax code, ID number and bank details on the application.
   */
  {
    path: 'e-warranty/dealer-registration/status/:id',
    loadComponent: () =>
      import('./module/dealer-registration/dealer-registration.component').then(
        (m) => m.DealerRegistrationComponent
      ),
  },
  {
    path: 'landing-page',
    redirectTo: '/admin/landing-page',
    pathMatch: 'full',
  },
  {
    path: '',
    component: LoginComponent,
    canActivate: [LoginGuard],
  },
  {
    path: '',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./module/intelux/intelux.routes').then(
        (m) => m.routes
      ),
  },

  {
    path: 'image-display/:containerName/:filename',
    component: ImageDisplayComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'image-viewer',
    loadComponent: () =>
      import('./module/sellout-invoice-viewer/sellout-invoice-viewer.component').then(
        (m) => m.SelloutInvoiceViewerComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'liveQuiz',
    loadComponent: () =>
      import('./module/Training/components/live-quiz/live-quiz.component').then(
        (m) => m.LiveQuizComponent
      ),
  },
  {
    path: 'sellout-dashboard-preview',
    loadComponent: () =>
      import('./module/master-management/sellout/dashboard/dashboard.component').then(
        (m) => m.DashboardComponent
      ),
  },
  {
    path: 'journey-dashboard-view',
    loadComponent: () =>
      import('./module/journey-plan/journey-plan-dashboard/journey-plan-dashboard.component').then(
        (m) => m.JourneyPlanDashboardComponent
      ),
    canActivate: [AuthGuard],
    data: { standalone: true },
  },
  { path: '**', redirectTo: '/' }
  // {
  //   path: 'user',
  //   loadChildren: () =>
  //     import('./module/user-management/user-management.routes').then(
  //       (m) => m.routes
  //     ),
  // },
  // {
  //   path: 'store',
  //   loadChildren: () =>
  //     import('./module/store-management/store-management.routes').then(
  //       (m) => m.routes
  //     ),
  // },
  // {
  //   path: 'survey',
  //   loadChildren: () =>
  //     import('./module/survey-management/survey-management.routes').then(
  //       (m) => m.routes
  //     ),
  // },
];
